import classNames from 'classnames';
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import Icon from '../Icon';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  error?: string;
  value: boolean;
  isRequired?: boolean;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, id = Math.random().toString(), error, isRequired, disabled, ...props }, ref) => (
    <div className="flex items-center gap-2 font-noto leading-snug">
      <div
        className={classNames(
          'relative flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-[4px] border border-grey-30 shadow-xs outline-none transition-shadow',
          // Keyed off the hidden input's :focus-visible so a mouse click doesn't leave a ring behind.
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focus has-[:focus-visible]:ring-offset-1 has-[:focus-visible]:ring-offset-white',
          props.checked && 'bg-primary border-primary text-primary-foreground',
          disabled && 'cursor-not-allowed opacity-50',
          error && 'border-red-50 ring-red-50/20',
        )}
      >
        <input
          {...props}
          ref={ref}
          id={id}
          type="checkbox"
          checked={props.checked}
          value={String(props.value)}
          disabled={disabled}
          onChange={(e) => props.onChange?.(e.target.checked)}
          className="absolute inset-0 cursor-pointer opacity-0"
          aria-invalid={!!error}
          aria-required={isRequired || undefined}
        />
        {props.checked && <Icon icon="check" size={16} className="pointer-events-none text-current" />}
      </div>

      {label && (
        <label className="cursor-pointer text-xs capitalize" htmlFor={id}>
          <span className="space-x-1">
            <span>{label}</span>
            <span aria-hidden="true" className="text-red-500">
              {isRequired ? '*' : ' '}
            </span>
          </span>
        </label>
      )}
    </div>
  ),
);

export default Checkbox;
